import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import HomePage from "@/pages/HomePage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ArchivePage from "@/pages/ArchivePage";
import OurStoryPage from "@/pages/OurStoryPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import FAQPage from "@/pages/FAQPage";
import ScrollToTop from "@/components/ScrollToTop";
import LeadMagnetPopup from "@/components/LeadMagnetPopup";
import StickyLeadBar from "@/components/StickyLeadBar";
import FreeGuidePage from "@/pages/FreeGuidePage";
import TermsPage from "@/pages/TermsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

// Strips trailing slashes: /article/xyz/ -> /article/xyz (fixes duplicate content).
function TrailingSlashRedirect() {
  const { pathname } = useLocation();
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") || "/"} replace />;
  }
  return null;
}

// Default canonical for every page — absolute URL on the production origin,
// no trailing slash. Pages with SeoHead override og:url; canonical lives here only.
function CanonicalTag() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "");
  // On React 19, react-helmet-async v3 renders head tags as React elements and
  // React hoists them into <head> natively — it does NOT reuse or remove the
  // static tags shipped in index.html / prerendered HTML, so duplicates appear.
  // All build-time tags carry data-static-head="true"; React's hoisted tags never
  // do. A MutationObserver on <head> keeps exactly one of each tag — preferring
  // React's (unmarked) copy, which is the live per-route value — no matter when
  // tags land or where React inserts them.
  useEffect(() => {
    const SELECTORS = [
      "title",
      'link[rel="canonical"]',
      'meta[name="description"]',
      'meta[name="theme-color"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:url"]',
      'meta[property="og:type"]',
      'meta[property="og:site_name"]',
      'meta[property="og:image"]',
      'meta[name="twitter:card"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]',
    ];
    const dedupe = () => {
      SELECTORS.forEach((sel) => {
        const tags = Array.from(document.head.querySelectorAll(sel));
        if (tags.length < 2) return;
        const statics = tags.filter((el) => el.hasAttribute("data-static-head"));
        const fresh = tags.filter((el) => !el.hasAttribute("data-static-head"));
        if (fresh.length) {
          // React's copy exists — every static/build-time copy is stale.
          statics.forEach((el) => el.remove());
          fresh.slice(1).forEach((el) => el.remove());
        } else {
          // Only build-time copies (pre-hydration): keep the first, drop the rest.
          tags.slice(1).forEach((el) => el.remove());
        }
      });
    };
    dedupe();
    const obs = new MutationObserver(dedupe);
    obs.observe(document.head, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [pathname]);
  return (
    <Helmet>
      <link rel="canonical" href={`https://summarymapsjournal.com${path}`} />
    </Helmet>
  );
}

// Site-wide layout: header + footer on every public route (admin stays bare).
function SiteLayout() {
  return (
    <>
      <CanonicalTag />
      <SiteHeader />
      <Outlet />
      <SiteFooter />
    </>
  );
}

// Popup + sticky bar are suppressed on /free-guide — that page IS the capture form.
function GlobalLeadCapture() {
  const { pathname } = useLocation();
  if (pathname === "/free-guide") return null;
  return (
    <>
      <LeadMagnetPopup />
      <StickyLeadBar />
    </>
  );
}
import { AuthProvider } from "@/lib/auth";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <TrailingSlashRedirect />
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/article/:slug" element={<ArticleDetailPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/archive" element={<ArchivePage />} />
                <Route path="/why-we-built-summarymaps" element={<OurStoryPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
                <Route path="/terms-of-service" element={<TermsPage />} />
                <Route path="/free-guide" element={<FreeGuidePage />} />
                {/* Legacy/empty patterns — redirect to valid pages (soft-404 fix) */}
                <Route path="/category/*" element={<Navigate to="/archive" replace />} />
                <Route path="/tags" element={<Navigate to="/archive" replace />} />
                <Route path="/tag/*" element={<Navigate to="/archive" replace />} />
                <Route path="/author/*" element={<Navigate to="/about" replace />} />
                <Route path="/page/*" element={<Navigate to="/" replace />} />
                <Route path="/search" element={<Navigate to="/" replace />} />
                {/* Catch-all: real 404 UI with noindex for everything else */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminDashboardPage />} />
            </Routes>
            <GlobalLeadCapture />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
